import React, { useState, useEffect } from 'react';
import { 
    verifyLehrerPin, 
    identifyLehrer, 
    getLehrerData,
    createLehrerAngebot,
    updateLehrerAngebot,
    deleteLehrerAngebot,
    updateLehrerProfile,
    getPublicSettings
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
    Music, 
    Clock, 
    Plus, 
    Trash2,
    LogOut,
    CheckCircle,
    MapPin,
    Calendar,
    Check,
    Pencil,
    ArrowRight,
    ChevronDown,
    AlertTriangle,
    Lock,
    Download,
    FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Zeit-Optionen
const HOURS = Array.from({ length: 13 }, (_, i) => i + 9); // 9-21
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

const WOCHENTAGE = [
    { id: 'Montag', short: 'Mo', label: 'Montag' },
    { id: 'Dienstag', short: 'Di', label: 'Dienstag' },
    { id: 'Mittwoch', short: 'Mi', label: 'Mittwoch' },
    { id: 'Donnerstag', short: 'Do', label: 'Donnerstag' },
    { id: 'Freitag', short: 'Fr', label: 'Freitag' },
    { id: 'Samstag', short: 'Sa', label: 'Samstag' }
];

export function LehrerPortal() {
    const [step, setStep] = useState(1);
    const [pin, setPin] = useState('');
    const [vorname, setVorname] = useState('');
    const [nachname, setNachname] = useState('');
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const [lehrperson, setLehrperson] = useState(null);
    const [angebote, setAngebote] = useState([]);
    const [options, setOptions] = useState({ instrumente: [], unterrichtsorte: [], gemeinden: [] });
    const [deadline, setDeadline] = useState(null);
    const [koordinatorGemeinden, setKoordinatorGemeinden] = useState([]);
    const [portalSettings, setPortalSettings] = useState({
        lehrer_can_edit: true,
        lehrer_can_delete: false
    });
    
    // Sheet state
    const [sheetOpen, setSheetOpen] = useState(false);
    const [editingAngebot, setEditingAngebot] = useState(null);
    const [deleteAngebot, setDeleteAngebot] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    
    // Form state - Gemeinde → Ort → Raum → Instrument(e) → Tag → Zeitslots
    const [selectedGemeinde, setSelectedGemeinde] = useState(null);
    const [selectedOrt, setSelectedOrt] = useState(null);
    const [selectedRaum, setSelectedRaum] = useState(null);
    const [selectedInstruments, setSelectedInstruments] = useState([]); // Multiple instruments
    const [selectedTag, setSelectedTag] = useState(null);
    // Support multiple time slots with h/m separated
    const [zeitSlots, setZeitSlots] = useState([{ vonH: '', vonM: '', bisH: '', bisM: '' }]);
    
    // UI state
    const [showAllGemeinden, setShowAllGemeinden] = useState(false);
    const [showAllInstruments, setShowAllInstruments] = useState(false);
    
    useEffect(() => {
        const savedToken = sessionStorage.getItem('lehrer_token');
        if (savedToken) {
            setToken(savedToken);
            setStep(3);
            loadData(savedToken);
        }
        // Load portal settings
        loadPortalSettings();
    }, []);
    
    const loadPortalSettings = async () => {
        try {
            const response = await getPublicSettings();
            if (response.data) {
                setPortalSettings({
                    lehrer_can_edit: response.data.lehrer_can_edit ?? true,
                    lehrer_can_delete: response.data.lehrer_can_delete ?? false
                });
            }
        } catch (err) {
            // Use defaults if settings can't be loaded
        }
    };
    
    const loadData = async (t) => {
        try {
            setLoading(true);
            const response = await getLehrerData(t);
            setLehrperson(response.data.lehrperson);
            setAngebote(response.data.angebote);
            setOptions(response.data.options);
            setDeadline(response.data.deadline || null);
            setKoordinatorGemeinden(response.data.koordinator_gemeinden || []);
        } catch (error) {
            toast.error('Sitzung abgelaufen');
            handleLogout();
        } finally {
            setLoading(false);
        }
    };
    
    const handlePinSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await verifyLehrerPin(pin);
            // Save the temporary token for the next step
            setToken(response.data.token);
            setStep(2);
        } catch (error) {
            toast.error('Ungültiger PIN');
        } finally {
            setLoading(false);
        }
    };
    
    const handleNameSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await identifyLehrer(vorname, nachname, token);
            const newToken = token; // Keep using the same token
            sessionStorage.setItem('lehrer_token', newToken);
            setStep(3);
            loadData(newToken);
            toast.success(`Willkommen, ${response.data.lehrer.vorname}!`);
        } catch (error) {
            toast.error('Name nicht gefunden');
        } finally {
            setLoading(false);
        }
    };
    
    const handleLogout = () => {
        sessionStorage.removeItem('lehrer_token');
        setToken(null);
        setStep(1);
        setPin('');
        setVorname('');
        setNachname('');
        setLehrperson(null);
        setAngebote([]);
    };
    
    // Add gemeinde to teacher's profile
    const addGemeindeToLehrer = async (gemeindeId) => {
        if (!lehrperson || myGemeinden.includes(gemeindeId)) return;
        
        const updatedGemeinden = [...myGemeinden, gemeindeId];
        try {
            await updateLehrerProfile({ gemeinden: updatedGemeinden }, token);
            setLehrperson({ ...lehrperson, gemeinden: updatedGemeinden });
            toast.success('Gemeinde hinzugefügt');
        } catch (error) {
            toast.error('Fehler beim Speichern');
            console.error(error);
        }
    };
    
    // Add instrument to teacher's profile
    const addInstrumentToLehrer = async (instrumentId) => {
        if (!lehrperson || (lehrperson.instrumente || []).includes(instrumentId)) return;
        
        const updatedInstrumente = [...(lehrperson.instrumente || []), instrumentId];
        try {
            await updateLehrerProfile({ instrumente: updatedInstrumente }, token);
            // Reload data to get updated instrument names
            await loadData(token);
            toast.success('Instrument hinzugefügt');
        } catch (error) {
            toast.error('Fehler beim Speichern');
            console.error(error);
        }
    };
    
    // Open sheet for NEW offer
    const openNewOfferSheet = () => {
        setEditingAngebot(null);
        setSelectedGemeinde(null);
        setSelectedOrt(null);
        setSelectedRaum(null);
        // Default: NO instruments selected - user must choose
        setSelectedInstruments([]);
        setSelectedTag(null);
        setZeitSlots([{ vonH: '', vonM: '', bisH: '', bisM: '' }]);
        setShowAllGemeinden(false);
        setSheetOpen(true);
    };
    
    // Open sheet for EDITING offer
    const openEditOfferSheet = (angebot) => {
        setEditingAngebot(angebot);
        setSelectedGemeinde(angebot.gemeinde_id);
        setSelectedOrt(angebot.unterrichtsort_id);
        setSelectedRaum(angebot.raum_id || null);
        // Set instruments from the offer
        setSelectedInstruments(angebot.instrument_ids || (angebot.instrument_id ? [angebot.instrument_id] : []));
        setSelectedTag(angebot.wochentag);
        // Parse time strings to slot structure - support both ":" and "." separators
        const parseTime = (timeStr) => {
            if (!timeStr) return { h: '', m: '' };
            // Split by ":" or "."
            const parts = timeStr.split(/[:\.]/);
            if (parts.length < 2) return { h: '', m: '' };
            return { h: parseInt(parts[0]).toString(), m: parseInt(parts[1]).toString() };
        };
        const vonParsed = parseTime(angebot.zeit_von);
        const bisParsed = parseTime(angebot.zeit_bis);
        setZeitSlots([{ 
            vonH: vonParsed.h, vonM: vonParsed.m, 
            bisH: bisParsed.h, bisM: bisParsed.m 
        }]);
        setShowAllGemeinden(false);
        setSheetOpen(true);
    };
    
    const closeSheet = () => {
        setSheetOpen(false);
        setEditingAngebot(null);
    };
    
    // Toggle instrument selection
    const toggleInstrument = (instId) => {
        if (selectedInstruments.includes(instId)) {
            // Don't allow deselecting all
            if (selectedInstruments.length > 1) {
                setSelectedInstruments(selectedInstruments.filter(id => id !== instId));
            }
        } else {
            setSelectedInstruments([...selectedInstruments, instId]);
        }
    };
    
    // Add another time slot
    const addTimeSlot = () => {
        setZeitSlots([...zeitSlots, { vonH: '', vonM: '', bisH: '', bisM: '' }]);
    };
    
    // Remove a time slot
    const removeTimeSlot = (index) => {
        if (zeitSlots.length > 1) {
            setZeitSlots(zeitSlots.filter((_, i) => i !== index));
        }
    };
    
    // Update a time slot - auto-fill endzeit when startzeit is set
    const updateTimeSlot = (index, field, value) => {
        const newSlots = [...zeitSlots];
        newSlots[index][field] = value;
        
        // When vonH (start hour) is set, auto-fill bisH (end hour) to same value if empty
        if (field === 'vonH' && value) {
            if (!newSlots[index].bisH) {
                newSlots[index].bisH = value;
            }
        }
        // When vonM (start minute) is set, auto-fill bisM (end minute) to same value if empty
        if (field === 'vonM' && value !== '') {
            if (newSlots[index].bisM === '' || newSlots[index].bisM === undefined) {
                newSlots[index].bisM = value;
            }
        }
        
        setZeitSlots(newSlots);
    };
    
    // Validate that end time is not before start time
    const isTimeValid = (slot) => {
        if (!slot.vonH || slot.vonM === '' || !slot.bisH || slot.bisM === '') {
            return true; // Not complete yet, don't show error
        }
        const vonMinutes = parseInt(slot.vonH) * 60 + parseInt(slot.vonM);
        const bisMinutes = parseInt(slot.bisH) * 60 + parseInt(slot.bisM);
        return bisMinutes > vonMinutes;
    };
    
    // Format time slot to HH:MM string
    const formatTime = (h, m) => {
        if (!h || m === undefined || m === '') return '';
        return `${h.padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };
    
    // Check if a slot is complete
    const isSlotComplete = (slot) => {
        return slot.vonH && slot.vonM !== '' && slot.bisH && slot.bisM !== '';
    };
    
    const handleSaveOffer = async () => {
        // Validate slots - convert to von/bis format
        const validSlots = zeitSlots
            .filter(isSlotComplete)
            .map(s => ({
                von: formatTime(s.vonH, s.vonM),
                bis: formatTime(s.bisH, s.bisM)
            }));
        
        // Check time validity
        const invalidSlots = zeitSlots.filter(s => isSlotComplete(s) && !isTimeValid(s));
        if (invalidSlots.length > 0) {
            toast.error('Endzeit muss nach der Startzeit liegen');
            return;
        }
        
        if (!selectedOrt || !selectedRaum || !selectedTag || validSlots.length === 0) {
            toast.error('Bitte alle Felder ausfüllen');
            return;
        }
        
        if (selectedInstruments.length === 0) {
            toast.error('Bitte mindestens ein Instrument auswählen');
            return;
        }
        
        // Use selected instruments - must have at least one
        const instrumentIds = selectedInstruments;
        
        try {
            setLoading(true);
            
            if (editingAngebot) {
                // Update existing offer with first slot
                await updateLehrerAngebot(editingAngebot.id, {
                    instrument_ids: instrumentIds,
                    unterrichtsort_id: selectedOrt,
                    raum_id: selectedRaum || null,
                    lehrperson_id: lehrperson.id,
                    wochentag: selectedTag,
                    zeit_von: validSlots[0].von,
                    zeit_bis: validSlots[0].bis,
                    bemerkung: ''
                }, token);
            } else {
                // Create new offers for each time slot
                for (const slot of validSlots) {
                    await createLehrerAngebot({
                        instrument_ids: instrumentIds,
                        unterrichtsort_id: selectedOrt,
                        raum_id: selectedRaum || null,
                        lehrperson_id: lehrperson.id,
                        wochentag: selectedTag,
                        zeit_von: slot.von,
                        zeit_bis: slot.bis,
                        bemerkung: ''
                    }, token);
                }
            }
            
            closeSheet();
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 1500);
            loadData(token);
        } catch (error) {
            toast.error('Fehler beim Speichern');
        } finally {
            setLoading(false);
        }
    };
    
    const handleDelete = async () => {
        try {
            await deleteLehrerAngebot(deleteAngebot.id, token);
            toast.success('Angebot gelöscht');
            setDeleteAngebot(null);
            loadData(token);
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };
    
    // Computed values
    const myGemeinden = lehrperson?.gemeinden || [];
    const filteredOrte = selectedGemeinde 
        ? options.unterrichtsorte.filter(o => o.gemeinde_id === selectedGemeinde)
        : [];
    const filteredRaeume = selectedOrt 
        ? (options.raeume || []).filter(r => r.unterrichtsort_id === selectedOrt)
        : [];
    
    // Check if at least one valid time slot exists
    const hasValidTimeSlot = zeitSlots.some(isSlotComplete);
    const canSave = selectedOrt && selectedRaum && selectedTag && hasValidTimeSlot;
    
    const getGemeindeName = (id) => options.gemeinden.find(g => g.id === id)?.name || '';
    const getOrtName = (id) => options.unterrichtsorte.find(o => o.id === id)?.name || '';
    const getRaumName = (id) => {
        const raum = (options.raeume || []).find(r => r.id === id);
        return raum ? (raum.anzeigename || raum.bezeichnung) : '';
    };
    const getInstrumentName = (id) => {
        const idx = lehrperson?.instrumente?.indexOf(id);
        return idx >= 0 ? lehrperson?.instrumente_names?.[idx] : '';
    };

    // === PIN Entry ===
    if (step === 1) {
        return (
            <div className="min-h-screen bg-white flex flex-col">
                <header className="p-6 flex justify-center">
                    <img src="/logo.svg" alt="Musikschule" className="h-12" />
                </header>
                
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="w-full max-w-sm">
                        <div className="text-center mb-8">
                            <h1 className="text-2xl font-bold text-foreground">Lehrer-Portal</h1>
                            <p className="text-muted-foreground mt-2">Woche des offenen Unterrichts</p>
                        </div>
                        
                        <form onSubmit={handlePinSubmit} className="space-y-6">
                            <div>
                                <Label htmlFor="pin" className="text-base font-semibold mb-2 block">PIN eingeben</Label>
                                <Input
                                    id="pin"
                                    type="password"
                                    value={pin}
                                    onChange={(e) => setPin(e.target.value)}
                                    className="h-14 text-center text-2xl tracking-[0.5em] font-mono border-2"
                                    placeholder="••••"
                                    maxLength={4}
                                    autoFocus
                                    data-testid="pin-input"
                                />
                                <p className="text-sm text-muted-foreground mt-3 text-center">
                                    Den PIN bekommst du von der Musikschule
                                </p>
                            </div>
                            <Button 
                                type="submit" 
                                className="w-full h-14 text-lg font-semibold rounded-full" 
                                disabled={pin.length < 4 || loading} 
                                data-testid="pin-submit"
                            >
                                {loading ? 'Prüfen...' : 'Weiter'}
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }
    
    // === Name Entry ===
    if (step === 2) {
        return (
            <div className="min-h-screen bg-white flex flex-col">
                <header className="p-6 flex justify-center">
                    <img src="/logo.svg" alt="Musikschule" className="h-12" />
                </header>
                
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="w-full max-w-sm">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                            <h1 className="text-2xl font-bold text-foreground">PIN korrekt!</h1>
                            <p className="text-muted-foreground mt-2">Wie heißt du?</p>
                        </div>
                        
                        <form onSubmit={handleNameSubmit} className="space-y-6">
                            <div>
                                <Label htmlFor="vorname" className="text-base font-semibold mb-2 block">Vorname</Label>
                                <Input 
                                    id="vorname" 
                                    value={vorname} 
                                    onChange={(e) => setVorname(e.target.value)}
                                    onInput={(e) => setVorname(e.target.value)}
                                    onBlur={(e) => setVorname(e.target.value)}
                                    className="h-14 text-lg border-2" 
                                    placeholder="z.B. Maria"
                                    autoFocus 
                                    data-testid="vorname-input" 
                                />
                            </div>
                            <div>
                                <Label htmlFor="nachname" className="text-base font-semibold mb-2 block">Nachname</Label>
                                <Input 
                                    id="nachname" 
                                    value={nachname} 
                                    onChange={(e) => setNachname(e.target.value)}
                                    onInput={(e) => setNachname(e.target.value)}
                                    onBlur={(e) => setNachname(e.target.value)}
                                    className="h-14 text-lg border-2" 
                                    placeholder="z.B. Müller"
                                    data-testid="nachname-input" 
                                />
                            </div>
                            <Button 
                                type="submit" 
                                className="w-full h-14 text-lg font-semibold rounded-full" 
                                disabled={!vorname || !nachname || loading} 
                                data-testid="name-submit"
                            >
                                {loading ? 'Suchen...' : 'Anmelden'}
                            </Button>
                            <button 
                                type="button" 
                                onClick={() => { setStep(1); setPin(''); }} 
                                className="w-full text-base text-muted-foreground hover:text-foreground py-2"
                            >
                                ← Zurück
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }
    
    // === Main Dashboard ===
    return (
        <div className="min-h-screen bg-slate-50 pb-28">
            {/* Success Overlay */}
            {showSuccess && (
                <div className="fixed inset-0 bg-white/95 z-50 flex items-center justify-center animate-in fade-in duration-200">
                    <div className="text-center">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-in zoom-in duration-300">
                            <CheckCircle className="w-10 h-10 text-green-600" />
                        </div>
                        <h2 className="text-xl font-bold text-green-700">Gespeichert!</h2>
                    </div>
                </div>
            )}
            
            {/* Header */}
            <header className="bg-white border-b sticky top-0 z-10">
                <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
                    <img src="/logo.svg" alt="Musikschule" className="h-8" />
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-medium">{lehrperson?.vorname}</span>
                        <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground" data-testid="logout-btn">
                            <LogOut className="w-5 h-5" />
                        </Button>
                    </div>
                </div>
            </header>
            
            <main className="max-w-2xl mx-auto px-4 py-6">
                {/* Deadline Warning/Lock Banner */}
                {deadline?.locked && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                        <div className="flex items-start gap-3">
                            <Lock className="w-5 h-5 text-red-600 mt-0.5" />
                            <div>
                                <p className="font-semibold text-red-800">Eintragungsfrist abgelaufen</p>
                                <p className="text-sm text-red-700">{deadline.message}</p>
                            </div>
                        </div>
                    </div>
                )}
                
                {deadline?.warning && !deadline?.locked && (
                    <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                            <div>
                                <p className="font-semibold text-amber-800">Achtung: Frist läuft ab!</p>
                                <p className="text-sm text-amber-700">{deadline.message}</p>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Koordinator Banner */}
                {koordinatorGemeinden.length > 0 && (
                    <div className="mb-6 bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-200 p-4">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                <FileText className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div className="flex-1">
                                <p className="font-semibold text-emerald-800 mb-1">
                                    Du bist Koordinator für {koordinatorGemeinden.length === 1 ? 'diese Gemeinde' : 'diese Gemeinden'}:
                                </p>
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {koordinatorGemeinden.map(g => (
                                        <span key={g.id} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-sm font-medium">
                                            {g.name}
                                        </span>
                                    ))}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {koordinatorGemeinden.map(g => (
                                        <a 
                                            key={g.id}
                                            href={`${process.env.REACT_APP_BACKEND_URL}/api/lehrer/koordinator/pdf/${g.id}?token=${token}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
                                        >
                                            <Download className="w-4 h-4" />
                                            {g.name} PDF
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Welcome */}
                <div className="mb-6">
                    <h1 className="text-xl font-bold">Hallo {lehrperson?.vorname}!</h1>
                    <p className="text-muted-foreground">
                        Du hast <strong>{angebote.length}</strong> Angebot{angebote.length !== 1 ? 'e' : ''} eingetragen.
                    </p>
                </div>
                
                {/* Instruments - More prominent */}
                {lehrperson?.instrumente_names?.length > 0 && (
                    <div className="bg-gradient-to-br from-violet-50 to-white rounded-xl p-4 border border-violet-100 mb-6">
                        <p className="text-sm text-violet-600 font-medium mb-2 flex items-center gap-2">
                            <Music className="w-4 h-4" />
                            Deine Instrumente
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {lehrperson.instrumente_names.map((name, idx) => (
                                <span 
                                    key={idx}
                                    className="px-3 py-1.5 bg-violet-600 text-white rounded-full text-sm font-semibold"
                                >
                                    {name}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* === MY OFFERS === */}
                <div data-testid="my-offers-section">
                    <h2 className="text-lg font-bold mb-4">Meine Angebote</h2>
                    
                    {loading && angebote.length === 0 ? (
                        <div className="space-y-3">
                            {[1,2,3].map(i => (
                                <div key={i} className="bg-white rounded-xl p-5 animate-pulse border">
                                    <div className="h-5 bg-muted rounded w-1/3 mb-2" />
                                    <div className="h-4 bg-muted rounded w-1/2" />
                                </div>
                            ))}
                        </div>
                    ) : angebote.length === 0 ? (
                        <div className="bg-white rounded-xl p-8 text-center border">
                            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                            <h3 className="font-semibold text-lg mb-2">Noch keine Angebote</h3>
                            <p className="text-muted-foreground mb-4">
                                Trage dein erstes Angebot ein!
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {WOCHENTAGE.map(tag => {
                                const tagAngebote = angebote.filter(a => a.wochentag === tag.id);
                                if (tagAngebote.length === 0) return null;
                                
                                return (
                                    <div key={tag.id}>
                                        <div className="flex items-center gap-2 mb-3">
                                            <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center text-sm font-bold">
                                                {tag.short}
                                            </span>
                                            <span className="font-semibold">{tag.label}</span>
                                        </div>
                                        <div className="space-y-2">
                                            {tagAngebote.map(a => {
                                                // Get instrument names for this offer
                                                const instrumentNames = a.instrument_ids 
                                                    ? a.instrument_ids.map(id => {
                                                        const idx = lehrperson?.instrumente?.indexOf(id);
                                                        return idx >= 0 ? lehrperson?.instrumente_names?.[idx] : null;
                                                      }).filter(Boolean)
                                                    : a.instrument_id 
                                                        ? [(() => {
                                                            const idx = lehrperson?.instrumente?.indexOf(a.instrument_id);
                                                            return idx >= 0 ? lehrperson?.instrumente_names?.[idx] : a.instrument_name;
                                                          })()]
                                                        : [];
                                                
                                                return (
                                                <div 
                                                    key={a.id} 
                                                    onClick={() => !deadline?.locked && portalSettings.lehrer_can_edit && openEditOfferSheet(a)}
                                                    className={cn(
                                                        "bg-white rounded-xl p-4 border transition-colors",
                                                        (!deadline?.locked && portalSettings.lehrer_can_edit)
                                                            ? "hover:border-slate-300 cursor-pointer group"
                                                            : "cursor-default"
                                                    )}
                                                    data-testid={`offer-card-${a.id}`}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <Clock className="w-4 h-4 text-slate-400" />
                                                                <span className="font-semibold text-lg">
                                                                    {a.zeit_von} – {a.zeit_bis}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                                                                <MapPin className="w-3 h-3" />
                                                                {a.gemeinde_name} – {a.unterrichtsort_name}
                                                                {a.raum_name && <span className="text-xs text-slate-400">({a.raum_name})</span>}
                                                            </p>
                                                            {/* Instrument Chips */}
                                                            {instrumentNames.length > 0 && (
                                                                <div className="flex flex-wrap gap-1">
                                                                    {instrumentNames.map((instName, idx) => (
                                                                        <span 
                                                                            key={idx}
                                                                            className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full text-xs font-medium"
                                                                        >
                                                                            {instName}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {!deadline?.locked && (
                                                            <div className="flex items-center gap-2">
                                                                {portalSettings.lehrer_can_edit && (
                                                                    <Pencil className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                                )}
                                                                {portalSettings.lehrer_can_delete && (
                                                                    <button 
                                                                        onClick={(e) => { e.stopPropagation(); setDeleteAngebot(a); }}
                                                                        className="p-2 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all"
                                                                        data-testid={`delete-offer-${a.id}`}
                                                                    >
                                                                        <Trash2 className="w-5 h-5" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </main>
            
            {/* === STICKY FOOTER === */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t z-20">
                <div className="max-w-2xl mx-auto">
                    {deadline?.locked ? (
                        <div className="flex items-center justify-center gap-2 h-14 text-muted-foreground">
                            <Lock className="w-5 h-5" />
                            <span>Änderungen nicht mehr möglich</span>
                        </div>
                    ) : (
                        // Only show "New Offer" button for typ=lehrperson
                        lehrperson?.typ === 'lehrperson' || !lehrperson?.typ ? (
                            <Button 
                                onClick={openNewOfferSheet}
                                className="w-full h-14 text-lg font-semibold rounded-full shadow-lg"
                                data-testid="add-offer-btn"
                            >
                                <Plus className="w-5 h-5 mr-2" />
                                Neues Angebot eintragen
                            </Button>
                        ) : (
                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-center">
                                <p className="text-sm text-amber-700">
                                    Als {lehrperson?.typ === 'administration' ? 'Administrationskontakt' : 'externe Person'} können keine Angebote erstellt werden.
                                </p>
                            </div>
                        )
                    )}
                </div>
            </div>
            
            {/* === SHEET for Create/Edit === */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl overflow-y-auto">
                    <SheetHeader className="mb-6">
                        <SheetTitle className="text-xl font-bold">
                            {editingAngebot ? 'Angebot bearbeiten' : 'Neues Angebot'}
                        </SheetTitle>
                    </SheetHeader>
                    
                    <div className="space-y-6 pb-6">
                        {/* Step 1: Gemeinde auswählen */}
                        <div>
                            <Label className="text-base font-semibold mb-3 block">
                                <span className={cn(
                                    "w-6 h-6 rounded-full inline-flex items-center justify-center text-sm mr-2",
                                    selectedGemeinde ? "bg-emerald-600 text-white" : "bg-emerald-500 text-white"
                                )}>1</span>
                                Gemeinde auswählen {!selectedGemeinde && "👈"}
                            </Label>
                            
                            {/* My Gemeinden first */}
                            <div className="flex flex-wrap gap-2 mb-2">
                                {options.gemeinden.filter(g => myGemeinden.includes(g.id)).map(gem => (
                                    <button
                                        key={gem.id}
                                        onClick={() => { setSelectedGemeinde(gem.id); setSelectedOrt(null); }}
                                        data-testid={`sheet-gemeinde-${gem.id}`}
                                        className={cn(
                                            "px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all",
                                            selectedGemeinde === gem.id
                                                ? "bg-emerald-600 border-emerald-600 text-white"
                                                : "bg-emerald-50 border-emerald-200 text-emerald-800 hover:border-emerald-400 hover:bg-emerald-100"
                                        )}
                                    >
                                        {selectedGemeinde === gem.id && <Check className="w-4 h-4 inline mr-1" />}
                                        {gem.name}
                                    </button>
                                ))}
                                
                                {myGemeinden.length === 0 && !showAllGemeinden && (
                                    <p className="text-sm text-muted-foreground italic py-2">Noch keine Gemeinden hinterlegt</p>
                                )}
                            </div>
                            
                            {/* "+ Andere Gemeinde" Button */}
                            {!showAllGemeinden && options.gemeinden.filter(g => !myGemeinden.includes(g.id)).length > 0 && (
                                <button
                                    onClick={() => setShowAllGemeinden(true)}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-100 hover:bg-emerald-200 rounded-xl transition-colors"
                                >
                                    <Plus className="w-4 h-4" />
                                    {myGemeinden.length === 0 ? 'Gemeinde auswählen' : 'Andere Gemeinde'}
                                </button>
                            )}
                            
                            {/* All Gemeinden expanded */}
                            {showAllGemeinden && (
                                <div className="mt-3 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                                    <p className="text-sm font-medium text-emerald-700 mb-3">Gemeinde hinzufügen:</p>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {options.gemeinden.filter(g => !myGemeinden.includes(g.id)).map(gem => (
                                            <button
                                                key={gem.id}
                                                onClick={async () => { 
                                                    await addGemeindeToLehrer(gem.id);
                                                    setSelectedGemeinde(gem.id); 
                                                    setSelectedOrt(null); 
                                                    setShowAllGemeinden(false); 
                                                }}
                                                className={cn(
                                                    "px-3 py-2 rounded-lg text-sm border transition-all",
                                                    selectedGemeinde === gem.id
                                                        ? "bg-emerald-600 border-emerald-600 text-white"
                                                        : "bg-white border-emerald-200 text-emerald-800 hover:border-emerald-400"
                                                )}
                                            >
                                                <Plus className="w-3 h-3 inline mr-1" />
                                                {gem.name}
                                            </button>
                                        ))}
                                    </div>
                                    <button 
                                        onClick={() => setShowAllGemeinden(false)} 
                                        className="text-sm text-emerald-600 hover:text-emerald-800 flex items-center gap-1"
                                    >
                                        <ChevronDown className="w-4 h-4 rotate-180" /> Schließen
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        {/* Step 2: Unterrichtsort */}
                        <div className={cn(!selectedGemeinde && "opacity-40 pointer-events-none")}>
                            <Label className="text-base font-semibold mb-3 block">
                                <span className={cn(
                                    "w-6 h-6 rounded-full inline-flex items-center justify-center text-sm mr-2",
                                    selectedOrt ? "bg-amber-600 text-white" : "bg-amber-500 text-white"
                                )}>2</span>
                                Unterrichtsort in {getGemeindeName(selectedGemeinde) || '...'} {selectedGemeinde && !selectedOrt && "👈"}
                            </Label>
                            
                            {selectedGemeinde && filteredOrte.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {filteredOrte.map(ort => (
                                        <button
                                            key={ort.id}
                                            onClick={() => { setSelectedOrt(ort.id); setSelectedRaum(null); }}
                                            data-testid={`sheet-ort-${ort.id}`}
                                            className={cn(
                                                "px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all",
                                                selectedOrt === ort.id
                                                    ? "bg-amber-600 border-amber-600 text-white"
                                                    : "bg-amber-50 border-amber-200 text-amber-800 hover:border-amber-400 hover:bg-amber-100"
                                            )}
                                        >
                                            {selectedOrt === ort.id && <Check className="w-4 h-4 inline mr-1" />}
                                            {ort.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                            
                            {selectedGemeinde && filteredOrte.length === 0 && (
                                <p className="text-sm text-muted-foreground italic">Keine Unterrichtsorte in dieser Gemeinde</p>
                            )}
                        </div>
                        
                        {/* Step 2.5: Raum auswählen (optional - nur wenn mehrere Räume vorhanden) */}
                        {/* Step 3: Raum auswählen (Pflicht) */}
                        <div className={cn(!selectedOrt && "opacity-40 pointer-events-none")}>
                            <Label className="text-base font-semibold mb-3 block">
                                <span className={cn(
                                    "w-6 h-6 rounded-full inline-flex items-center justify-center text-sm mr-2",
                                    selectedRaum ? "bg-orange-600 text-white" : "bg-orange-500 text-white"
                                )}>3</span>
                                Raum wählen {selectedOrt && !selectedRaum && "👈"}
                            </Label>
                            {filteredRaeume.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {filteredRaeume.map(raum => (
                                        <button
                                            key={raum.id}
                                            onClick={() => setSelectedRaum(raum.id)}
                                            data-testid={`sheet-raum-${raum.id}`}
                                            className={cn(
                                                "px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all",
                                                selectedRaum === raum.id
                                                    ? "bg-orange-600 border-orange-600 text-white"
                                                    : "bg-orange-50 border-orange-200 text-orange-800 hover:border-orange-400 hover:bg-orange-100"
                                            )}
                                        >
                                            {selectedRaum === raum.id && <Check className="w-4 h-4 inline mr-1" />}
                                            {raum.anzeigename || raum.bezeichnung}
                                            {raum.ist_standard && <span className="ml-1 text-xs opacity-70">(Standard)</span>}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground italic">Keine Räume für diesen Unterrichtsort</p>
                            )}
                        </div>
                        
                        {/* Step 4: Instrument auswählen (Multi-Select Chips) */}
                        <div className={cn(!selectedRaum && "opacity-40 pointer-events-none")}>
                            <Label className="text-base font-semibold mb-3 block">
                                <span className={cn(
                                    "w-6 h-6 rounded-full inline-flex items-center justify-center text-sm mr-2",
                                    selectedInstruments.length > 0 ? "bg-violet-600 text-white" : "bg-violet-500 text-white"
                                )}>4</span>
                                Instrument{lehrperson?.instrumente?.length > 1 ? 'e' : ''} wählen {selectedRaum && selectedInstruments.length === 0 && "👈"}
                            </Label>
                            {lehrperson?.instrumente_names?.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {lehrperson.instrumente.map((instId, idx) => {
                                        const isSelected = selectedInstruments.includes(instId);
                                        return (
                                            <button
                                                key={instId}
                                                onClick={() => toggleInstrument(instId)}
                                                data-testid={`sheet-instrument-${instId}`}
                                                className={cn(
                                                    "px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all flex items-center gap-1.5",
                                                    isSelected
                                                        ? "bg-violet-600 border-violet-600 text-white"
                                                        : "bg-violet-50 border-violet-200 text-violet-800 hover:border-violet-400 hover:bg-violet-100"
                                                )}
                                            >
                                                {isSelected && <Check className="w-4 h-4" />}
                                                {lehrperson.instrumente_names[idx]}
                                            </button>
                                        );
                                    })}
                                    {/* Add instrument button */}
                                    <button
                                        onClick={() => setShowAllInstruments(!showAllInstruments)}
                                        className="px-4 py-2.5 rounded-xl text-sm font-medium border-2 border-dashed border-violet-300 text-violet-500 hover:border-violet-400 hover:bg-violet-50 transition-all flex items-center gap-1.5"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Instrument hinzufügen
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <p className="text-sm text-muted-foreground italic">Keine Instrumente hinterlegt</p>
                                    <button
                                        onClick={() => setShowAllInstruments(!showAllInstruments)}
                                        className="px-4 py-2.5 rounded-xl text-sm font-medium border-2 border-dashed border-violet-300 text-violet-500 hover:border-violet-400 hover:bg-violet-50 transition-all flex items-center gap-1.5"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Instrument hinzufügen
                                    </button>
                                </div>
                            )}
                            
                            {/* All instruments expanded */}
                            {showAllInstruments && (
                                <div className="mt-3 p-4 bg-violet-50 rounded-xl border border-violet-200">
                                    <p className="text-sm font-medium text-violet-700 mb-3">Instrument hinzufügen:</p>
                                    <div className="flex flex-wrap gap-2 mb-3 max-h-48 overflow-y-auto">
                                        {options.instrumente?.filter(i => !(lehrperson?.instrumente || []).includes(i.id)).map(inst => (
                                            <button
                                                key={inst.id}
                                                onClick={async () => { 
                                                    await addInstrumentToLehrer(inst.id);
                                                    setShowAllInstruments(false); 
                                                }}
                                                className="px-3 py-2 rounded-lg text-sm border transition-all bg-white border-violet-200 text-violet-800 hover:border-violet-400"
                                            >
                                                <Plus className="w-3 h-3 inline mr-1" />
                                                {inst.name}
                                            </button>
                                        ))}
                                    </div>
                                    <button 
                                        onClick={() => setShowAllInstruments(false)} 
                                        className="text-sm text-violet-600 hover:text-violet-800 flex items-center gap-1"
                                    >
                                        <ChevronDown className="w-4 h-4 rotate-180" /> Schließen
                                    </button>
                                </div>
                            )}
                            
                            <p className="text-xs text-muted-foreground mt-2">
                                {selectedInstruments.length === 0 
                                    ? "Bitte wähle mindestens ein Instrument"
                                    : selectedInstruments.length === lehrperson?.instrumente?.length
                                        ? "Alle Instrumente ausgewählt"
                                        : `${selectedInstruments.length} von ${lehrperson?.instrumente?.length || 0} Instrumenten ausgewählt`}
                            </p>
                        </div>
                        
                        {/* Step 5: Wochentag */}
                        <div className={cn(!selectedRaum && "opacity-40 pointer-events-none")}>
                            <Label className="text-base font-semibold mb-3 block">
                                <span className={cn(
                                    "w-6 h-6 rounded-full inline-flex items-center justify-center text-sm mr-2",
                                    selectedTag ? "bg-blue-600 text-white" : "bg-blue-500 text-white"
                                )}>5</span>
                                Wochentag wählen {selectedRaum && !selectedTag && "👈"}
                            </Label>
                            <div className="grid grid-cols-6 gap-2">
                                {WOCHENTAGE.map(tag => (
                                    <button
                                        key={tag.id}
                                        onClick={() => setSelectedTag(tag.id)}
                                        data-testid={`sheet-tag-${tag.id}`}
                                        className={cn(
                                            "h-12 rounded-xl text-sm font-medium border-2 transition-all",
                                            selectedTag === tag.id
                                                ? "bg-blue-600 border-blue-600 text-white"
                                                : "bg-blue-50 border-blue-200 text-blue-800 hover:border-blue-400 hover:bg-blue-100"
                                        )}
                                    >
                                        {tag.short}
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Step 6: Uhrzeiten (multiple slots) */}
                        <div className={cn(!selectedTag && "opacity-40 pointer-events-none")}>
                            <Label className="text-base font-semibold mb-3 block">
                                <span className={cn(
                                    "w-6 h-6 rounded-full inline-flex items-center justify-center text-sm mr-2",
                                    hasValidTimeSlot ? "bg-slate-800 text-white" : "bg-slate-600 text-white"
                                )}>6</span>
                                Uhrzeit{zeitSlots.length > 1 ? 'en' : ''} eingeben {selectedTag && !hasValidTimeSlot && "👈"}
                            </Label>
                            
                            <div className="space-y-4">
                                {zeitSlots.map((slot, index) => (
                                    <div key={index} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                                        {/* Von */}
                                        <div className="mb-3">
                                            <p className="text-xs text-muted-foreground mb-1.5 font-medium">Von</p>
                                            <div className="flex items-center gap-2">
                                                <Select 
                                                    value={slot.vonH} 
                                                    onValueChange={(v) => updateTimeSlot(index, 'vonH', v)}
                                                >
                                                    <SelectTrigger className="w-24 h-12 text-lg font-medium">
                                                        <SelectValue placeholder="Std" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {HOURS.map(h => (
                                                            <SelectItem key={h} value={h.toString()}>
                                                                {h.toString().padStart(2, '0')}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <span className="text-xl font-bold text-slate-400">:</span>
                                                <Select 
                                                    value={slot.vonM} 
                                                    onValueChange={(v) => updateTimeSlot(index, 'vonM', v)}
                                                >
                                                    <SelectTrigger className="w-24 h-12 text-lg font-medium">
                                                        <SelectValue placeholder="Min" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {MINUTES.map(m => (
                                                            <SelectItem key={m} value={m.toString()}>
                                                                {m.toString().padStart(2, '0')}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <span className="text-sm text-slate-400 ml-1">Uhr</span>
                                            </div>
                                        </div>
                                        
                                        {/* Bis */}
                                        <div className="flex items-end gap-3">
                                            <div className="flex-1">
                                                <p className="text-xs text-muted-foreground mb-1.5 font-medium">Bis</p>
                                                <div className="flex items-center gap-2">
                                                    <Select 
                                                        value={slot.bisH} 
                                                        onValueChange={(v) => updateTimeSlot(index, 'bisH', v)}
                                                    >
                                                        <SelectTrigger className="w-24 h-12 text-lg font-medium">
                                                            <SelectValue placeholder="Std" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {HOURS.map(h => (
                                                                <SelectItem key={h} value={h.toString()}>
                                                                    {h.toString().padStart(2, '0')}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <span className="text-xl font-bold text-slate-400">:</span>
                                                    <Select 
                                                        value={slot.bisM} 
                                                        onValueChange={(v) => updateTimeSlot(index, 'bisM', v)}
                                                    >
                                                        <SelectTrigger className="w-24 h-12 text-lg font-medium">
                                                            <SelectValue placeholder="Min" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {MINUTES.map(m => (
                                                                <SelectItem key={m} value={m.toString()}>
                                                                    {m.toString().padStart(2, '0')}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <span className="text-sm text-slate-400 ml-1">Uhr</span>
                                                </div>
                                            </div>
                                            
                                            {zeitSlots.length > 1 && (
                                                <button
                                                    onClick={() => removeTimeSlot(index)}
                                                    className="p-3 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            )}
                                        </div>
                                        
                                        {/* Time validation error */}
                                        {isSlotComplete(slot) && !isTimeValid(slot) && (
                                            <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                                                <span className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center text-xs">!</span>
                                                Endzeit muss nach der Startzeit liegen
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                            
                            {/* Add another time slot button - always visible */}
                            <button
                                onClick={addTimeSlot}
                                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-xl transition-colors"
                                data-testid="add-time-slot-btn"
                            >
                                <Plus className="w-4 h-4" />
                                Weiteren Zeitslot hinzufügen
                            </button>
                        </div>
                        
                        {/* Summary */}
                        {canSave && (
                            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                                <p className="text-green-800 text-center">
                                    <strong>{selectedTag}</strong>
                                    {zeitSlots.filter(isSlotComplete).map((s, i) => (
                                        <span key={i}>{i > 0 ? ' + ' : ', '}{formatTime(s.vonH, s.vonM)} – {formatTime(s.bisH, s.bisM)}</span>
                                    ))}
                                    <br />
                                    <span className="text-sm text-green-600">
                                        {selectedInstruments.length > 0 ? selectedInstruments.map(id => getInstrumentName(id)).filter(Boolean).join(', ') + ' • ' : ''}
                                        {getOrtName(selectedOrt)}{selectedRaum && ` (${getRaumName(selectedRaum)})`}, {getGemeindeName(selectedGemeinde)}
                                    </span>
                                </p>
                            </div>
                        )}
                        
                        {/* Save Button */}
                        <Button 
                            onClick={handleSaveOffer} 
                            disabled={!canSave || loading}
                            className="w-full h-14 text-lg font-semibold rounded-full"
                            data-testid="sheet-save-btn"
                        >
                            {loading ? 'Speichern...' : (editingAngebot ? 'Änderungen speichern' : (
                                zeitSlots.filter(s => s.von && s.bis).length > 1 
                                    ? `${zeitSlots.filter(s => s.von && s.bis).length} Angebote speichern`
                                    : 'Angebot speichern'
                            ))}
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>
            
            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteAngebot} onOpenChange={() => setDeleteAngebot(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Angebot löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteAngebot && (
                                <>
                                    <strong>{deleteAngebot.wochentag}</strong>, {deleteAngebot.zeit_von}–{deleteAngebot.zeit_bis}
                                    <br />
                                    {deleteAngebot.unterrichtsort_name}, {deleteAngebot.gemeinde_name}
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground rounded-full">
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default LehrerPortal;
